import { Card, Typography } from "@mui/material";
import CustomTypography from "../../../Components/Typography";
import Box from "@mui/material/Box";
import InputField from "../../../Components/InputField";
import muiStyles from "../styles/muiStyles";
import CustomSwitch from "../../../Components/CustomSwitch";
import { useFormikContext } from "formik";
import MultiSelectDropdown from "../../../Components/CustomDropdowns/MultiSelectDropdown";
import { Trans, useTranslation } from "react-i18next";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";

const ServiceSettings = ({
  isloading,
  zones,
  selectedAreas,
  setSelectedAreas,
  languages,
  selectedLanguages,
  setSelectedLanguages,
}) => {
  const { t } = useTranslation("page-business-settings", "dropdowns");

  const {
    cardStyles,
    fieldsLabelStyles,
    fieldsLabelLightStyles,
    offHoursSurchargeBoxStyles,
  } = muiStyles;
  const { setFieldValue, handleChange, values, touched, handleBlur, errors } =
    useFormikContext();

  const { afterHoursPriceEnabled, afterHoursPrice, afterHoursCurrency } =
    values;

  const currencySymbol = getCurrencySymbol(afterHoursCurrency);

  // Map zone names to translation keys
  const zoneTranslationKeys = {
    "North District": "north_district",
    "Haifa District": "haifa_district",
    "Jerusalem District": "jerusalem_district",
    "Tel Aviv District": "tel_aviv_district",
    "South District": "south_district",
    "Center District": "center_district",
  };

  // Map language names to translation keys
  const languageTranslationKeys = {
    English: "english",
    Hebrew: "hebrew",
    Russian: "russian",
  };

  return (
    <Card sx={cardStyles}>
      <CustomTypography type="subheading">
        {t("service_settings")}
      </CustomTypography>

      <Box sx={{ width: "100%", pt: "32px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: "2px",
            mb: "12px",
          }}
        >
          <Typography color="rgba(47, 43, 61, 0.9)" sx={fieldsLabelStyles}>
            {t("service_areas")}
          </Typography>
          <Typography color="rgba(47, 43, 61, 0.9)" sx={fieldsLabelLightStyles}>
            {t("service_areas_subtext")}
          </Typography>
        </Box>

        <Box sx={{ maxWidth: "345px" }}>
          <MultiSelectDropdown
            value={selectedAreas}
            onChange={setSelectedAreas}
            options={zones.map((zone) => ({
              value: zone.id,
              label: t(`dropdowns:zones.${zoneTranslationKeys[zone.name]}`),
            }))}
            placeholder={t("select_areas")}
            buttonText={t("areas_button")}
            disabled={isloading}
          />
        </Box>
      </Box>

      <Box sx={{ py: "32px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: "2px",
            mb: "12px",
          }}
        >
          <Typography color="rgba(47, 43, 61, 0.9)" sx={fieldsLabelStyles}>
            {t("service_languages")}
          </Typography>
          <Typography sx={fieldsLabelLightStyles}>
            {t("service_languages_subtext")}
          </Typography>
        </Box>

        <Box sx={{ maxWidth: "345px" }}>
          <MultiSelectDropdown
            value={selectedLanguages}
            onChange={setSelectedLanguages}
            options={languages.map((language) => ({
              value: language.name,
              label: t(
                `dropdowns:languages.${languageTranslationKeys[language.name]}`
              ),
            }))}
            placeholder={t("select_languages_placeholder")}
            showChips
            disabled={isloading}
            buttonHeight="auto"
            sx={{ button: { minHeight: "39px" } }}
          />
        </Box>
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            gap: "40px",
          }}
        >
          <Box sx={offHoursSurchargeBoxStyles}>
            <Typography color="rgba(47, 43, 61, 0.9)" sx={fieldsLabelStyles}>
              {t("off_hours_surcharge")}
            </Typography>
            <Typography
              color="rgba(47, 43, 61, 0.9)"
              sx={fieldsLabelLightStyles}
            >
              {t("off_hours_surcharge_subtext")}
            </Typography>
          </Box>

          <CustomSwitch
            checked={afterHoursPriceEnabled}
            onChange={(e) => {
              setFieldValue("afterHoursPriceEnabled", e.target.checked);
            }}
            switchTrackStyles={{
              backgroundColor: "rgba(165, 165, 165, 1)",
            }}
          />
        </Box>

        <Typography
          color="rgba(72, 72, 72, 0.7)"
          sx={{ fontSize: "12px", mt: "2px" }}
        >
          <Trans
            t={t}
            i18nKey="service_areas_subtext2"
            components={{ bdi: <bdi /> }}
          />
        </Typography>

        <Typography
          color="rgba(72, 72, 72, 0.7)"
          sx={{ fontSize: "12px", mt: "6px" }}
        >
          {t("off_hours_include_vat")}
        </Typography>

        {afterHoursPriceEnabled && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                mt: "16px",
              }}
            >
              <Typography
                color="rgba(71, 84, 103, 1)"
                sx={{ fontSize: "13px", fontWeight: 500 }}
              >
                {currencySymbol}
              </Typography>

              <InputField
                name="afterHoursPrice"
                type="number"
                value={afterHoursPrice}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="0"
                disabled={isloading}
                error={touched.afterHoursPrice && errors.afterHoursPrice}
                customSx={{
                  inputBaseRoot: {
                    width: "65px",
                  },
                }}
              />
            </Box>
            {touched.afterHoursPrice && errors.afterHoursPrice && (
              <Typography
                color="#d32f2f"
                sx={{ fontSize: "13px", mt: "4px", ml: "15px" }}
              >
                {errors.afterHoursPrice}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default ServiceSettings;
